/**
 * Module-level state to track the currently dragged card.
 * Used to share drag information between react-dnd (DashboardGroupColumn)
 * and native HTML5 drag targets (TabControl tabs).
 */
import type { ConversationDetail } from '@/types/conversation';

export interface DraggedCardInfo {
  conversationId: number;
  groupId: number; // DashboardGroupId (source column)
  dashboardId: number; // DashboardId (source dashboard)
  configValueId?: number; // ConfigValueId of the dragged card
  conversation?: ConversationDetail; // full object, set when dragging from InfoWindow
}

let _cardBeingDragged: DraggedCardInfo | null = null;
let _currentDashboardId: number = 0;

/** Registry of mounted card DOM elements, keyed by ConversationId */
const _cardElementRegistry = new Map<number, HTMLElement>();

export function registerCardElement(id: number, el: HTMLElement | null): void
{
  if (el) _cardElementRegistry.set(id, el);
  else _cardElementRegistry.delete(id);
}

export function getCardElement(id: number): HTMLElement | undefined
{
  return _cardElementRegistry.get(id);
}

/** Titles of all currently selected cards (across all columns), kept in sync by DashboardGroups */
let _selectedCardTitles: Array<{ id: number; title: string; layerColor?: string; groupId?: number }> = [];

export function setSelectedCardTitles(
  items: Array<{ id: number; title: string; layerColor?: string; groupId?: number }>,
): void
{
  _selectedCardTitles = items;
}

export function getSelectedCardTitles(): Array<{ id: number; title: string; layerColor?: string; groupId?: number }>
{
  return _selectedCardTitles;
}

export function setCardBeingDragged(info: DraggedCardInfo | null): void
{
  _cardBeingDragged = info;
  if (typeof document !== 'undefined')
  {
    if (info)
    {
      document.documentElement.classList.add('corgee-drag-active');
    }
    else
    {
      document.documentElement.classList.remove('corgee-drag-active');
    }
  }
}

export function getCardBeingDragged(): DraggedCardInfo | null
{
  return _cardBeingDragged;
}

/** Updated by the active page whenever the selected dashboard changes */
export function setCurrentDashboardId(id: number): void
{
  _currentDashboardId = id;
}

export function getCurrentDashboardId(): number
{
  return _currentDashboardId;
}

// ---------------------------------------------------------------------------
// Group drag state — shared between react-dnd (DashboardGroupColumn) and
// native HTML5 drop targets (TabControl tabs).
// ---------------------------------------------------------------------------

export interface DraggedGroupInfo {
  groupId: number;
  dashboardId: number;
  isDefault: boolean;
  groupName: string;
}

let _groupBeingDragged: DraggedGroupInfo | null = null;

export function setGroupBeingDragged(info: DraggedGroupInfo | null): void
{
  _groupBeingDragged = info;
}

export function getGroupBeingDragged(): DraggedGroupInfo | null
{
  return _groupBeingDragged;
}
